<template>
  <div class="step-lottery">
    <!-- 抽選中テキスト -->
    <div class="form-group">
      <label class="form-label">
        {{ '抽選中テキスト' }}
        <span class="required-badge">{{ '必須' }}</span>
      </label>
      <el-input
        v-model="lottery.text"
        type="textarea"
        :rows="3"
        class="lottery-text-field"
        :class="{ 'is-error': errors[3] && errors[3].text }"
        :placeholder="'ただいま抽選中です！'"
      />
      <div v-if="errors[3] && errors[3].text" class="input-invalid-text">
        {{ errors[3].text }}
      </div>
    </div>

    <!-- 抽選中メディア -->
    <div class="form-group">
      <label class="form-label">{{ '抽選中メディア' }}</label>
      <div class="lottery-media-section" v-loading="store.uploadLoading">
        <!-- プレビュー表示 -->
        <div v-if="lottery.url" class="media-preview">
          <i class="mdi mdi-close" @click.stop="handleMediaDelete"></i>
          <div class="media-name">{{ getMediaFileName() }}</div>
          <!-- 画像プレビュー -->
          <img v-if="isImageFile()" :src="lottery.url" alt="抽選中メディアプレビュー" class="direct-media" />
          <!-- 動画プレビュー -->
          <video v-else-if="!isImageFile()" :src="lottery.url" controls class="direct-media"></video>
        </div>

        <!-- ファイル選択ボタン（プレビューがない場合のみ表示） -->
        <div v-else class="media-upload-container">
          <el-button class="upload-btn" @click="handleMediaSelectClick">
            <i class="mdi mdi-image-plus" />
            {{ '画像・動画を追加' }}
          </el-button>
          <p class="upload-hint">
            {{ 'アップロード可能な最大サイズ: 画像8MB 動画25MB' }}
          </p>
          <!-- 非表示のファイル入力 -->
          <input
            id="lotteryMediaFileInput"
            type="file"
            accept="image/png,image/jpeg,video/mp4"
            style="display: none"
            @change="handleMediaFileChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { storeToRefs } from 'pinia'
import { getCurrentInstance, watch } from 'vue'

import { useInstagramScenarioEditorStore } from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'
import { TrackingService } from '@/client/services'

const { proxy } = getCurrentInstance() as any

// Store
const store = useInstagramScenarioEditorStore()
const { lottery, errors } = storeToRefs(store)

const getFileExtension = (name: string): string => {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index).toLowerCase() : ''
}

const isSupportedImageExtension = (name: string): boolean => {
  return ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name))
}

/**
 * メディアが画像かどうかを判定
 */
const isImageFile = (): boolean => {
  if (!lottery.value.url) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (lottery.value.fileType) {
    return lottery.value.fileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = lottery.value.url.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || lottery.value.url.startsWith('data:image/')
}

/**
 * メディアファイル名を取得
 */
const getMediaFileName = (): string => {
  // fileNameフィールドがあればそれを優先
  if (lottery.value.fileName) {
    return lottery.value.fileName
  }

  // blob URLの場合は拡張子から生成
  if (lottery.value.url && lottery.value.url.startsWith('blob:')) {
    const fileType = lottery.value.fileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (lottery.value.url) {
    const urlWithoutQuery = lottery.value.url.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return '抽選中メディア'
}

/**
 * メディア選択ボタンのクリックハンドラ
 */
const handleMediaSelectClick = () => {
  const fileInput = document.getElementById('lotteryMediaFileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

/**
 * メディアファイル変更ハンドラ
 */
const handleMediaFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック
  const imageTypes = ['image/png', 'image/jpeg']
  const videoTypes = ['video/mp4']

  const isImage = imageTypes.includes(file.type)
  const isVideo = videoTypes.includes(file.type)

  if (!isImage && !isVideo) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  if (isImage && !isSupportedImageExtension(file.name)) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    target.value = ''
    return
  }

  // ファイルサイズチェック（画像8MB未満、動画25MB未満）
  const maxSize = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024
  const sizeLabel = isImage ? '8MB' : '25MB'
  const fileTypeLabel = isImage ? '画像' : '動画'

  if (file.size >= maxSize) {
    proxy.$notify({
      title: `${fileTypeLabel}ファイルのサイズは${sizeLabel}未満にしてください。`,
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  // 画像/動画ともにAPIアップロードを実行し、mediaId を保存する
  try {
    const uploadResult = await store.uploadMedia(file)
    store.updateLottery({
      mediaId: uploadResult.mediaId,
      url: uploadResult.url,
      fileType: isImage ? 'image' : 'video',
      fileName: uploadResult.mediaOriginName || file.name
    })
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_lottery_media',
        screen: 'InstagramScenarioEditorDialog.StepLottery',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: 'lottery',
        fileType: isImage ? 'image' : 'video',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    proxy.$notify({
      title: 'メディアのアップロードに失敗しました',
      customClass: 'danger',
      duration: 5000
    })
  }

  // ファイル入力をリセット
  target.value = ''
}

/**
 * メディア削除ハンドラ
 */
const handleMediaDelete = () => {
  // Blob URLの場合はメモリ解放
  if (lottery.value.url && lottery.value.url.startsWith('blob:')) {
    URL.revokeObjectURL(lottery.value.url)
  }
  store.clearLotteryMedia()
}

/**
 * 抽選中テキストの変更を監視してリアルタイムバリデーション
 */
watch(
  lottery,
  () => {
    store.validateStep3()
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.step-lottery {
  padding: $spacing-unit * 2;
  border: 1px solid $border-color;
  border-radius: $round;

  .section-title {
    margin: 0 0 $spacing-unit * 2 0;
    font-size: 16px;
    font-weight: bold;
    color: $text-color;
  }

  .form-group {
    margin-bottom: $spacing-unit * 2;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-label {
    display: block;
    margin-bottom: $spacing-unit;
    font-size: 14px;
    font-weight: 500;
    color: $text-color;

    .required-badge {
      display: inline-block;
      padding: 2px 8px;
      margin-left: $spacing-unit;
      font-size: 12px;
      font-weight: normal;
      color: $text-color-white;
      background-color: $danger-color;
      border-radius: 12px;
    }
  }

  .form-description {
    margin-top: $spacing-unit;
    font-size: 12px;
    line-height: 1.5;
    color: $sub-text-color;
  }

  .input-invalid-text {
    margin-top: math.div($spacing-unit, 2);
    font-size: 14px;
    color: $danger-color;
  }

  .is-error {
    :deep(.el-textarea__inner) {
      border-color: $danger-color !important;
    }
  }

  // メディアアップロードセクション
  .lottery-media-section {
    position: relative;
    min-height: 120px;

    .media-upload-container {
      display: flex;
      flex-direction: row;
      align-items: center;

      .upload-btn {
        margin-right: $spacing-unit * 2;

        i {
          margin-right: math.div($spacing-unit, 2);
        }
      }

      .upload-hint {
        margin: 0;
        font-size: 12px;
        color: $sub-text-color;
      }
    }

    .media-preview {
      position: relative;
      display: inline-block;
      padding: math.div($spacing-unit, 2) $spacing-unit;
      border: 1px solid $border-color;
      border-radius: $round;

      .media-name {
        padding-right: $spacing-unit * 3;
        margin-bottom: $spacing-unit;
        font-size: 14px;
        font-weight: 500;
        color: $text-color;
        word-break: break-all;
      }

      .mdi-close {
        position: absolute;
        top: math.div($spacing-unit, 2);
        right: math.div($spacing-unit, 2);
        font-size: 18px;
        color: $text-color;
        cursor: pointer;

        &:hover {
          color: $danger-color;
        }
      }

      .direct-media {
        display: block;
        max-width: 100%;
        max-height: $spacing-unit * 32;
      }
    }
  }
}
</style>
